/**
 * Contemporary groups: Hondo proposes groups from the management group, birth window, sex and known
 * differences. A person can move a calf, with a reason, and save. Saving again keeps the old version.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { localDate } from "../../../contract";
import { ApiError, api } from "../../../web/lib/api";
import { formatDate } from "../../../web/lib/format";
import { plural, t, useLanguage } from "../../../web/lib/i18n";
import { invalidateAll, useApi } from "../../../web/lib/query";
import { useCan, useMe } from "../../../web/lib/session";
import { Button, Card, Chip, Empty, ErrorNote, Field, List, Loading, Row, Section, Segmented, Select, Sheet, TextInput, Toggle, toast } from "../../../web/ui/kit";
import type { CgProposal } from "../shared/contemporary";
import { CG_FACTOR_LABELS, SEX_CLASS_LABELS } from "../shared/labels";
import type { CgResponse } from "../shared/types";
import { GeneticsShell } from "./GeneticsWorkspace";

type Stage = "birth" | "weaning" | "yearling";

export function factorText(f: CgProposal["factors"][number]): string {
  if (f.kind === "sex") return t(SEX_CLASS_LABELS[f.value] ?? f.value);
  if (f.kind === "management") return f.value || t("No management group");
  if (f.kind === "window") {
    const [from, to] = f.value.split("..");
    return t("Born {{from}} to {{to}}", { from: formatDate(from), to: formatDate(to) });
  }
  return t(CG_FACTOR_LABELS[f.kind] ?? f.kind);
}

/** Apply hand moves to the proposal: each moved calf leaves its group and joins the chosen one. */
export function applyMoves(groups: CgProposal[], moves: Record<string, { to: string; reason: string }>) {
  return groups.map((g) => ({
    code: g.code, basis: g.factors.map(factorText).join(", "),
    members: [...g.members.filter((m) => !moves[m] || moves[m].to === g.code), ...Object.entries(moves).filter(([id, mv]) => mv.to === g.code && !g.members.includes(id)).map(([id]) => id)],
    moved: Object.entries(moves).filter(([id, mv]) => mv.to === g.code && !g.members.includes(id)).map(([animal_id, mv]) => ({ animal_id, reason: mv.reason })),
  })).filter((g) => g.members.length);
}

export function ContemporaryPage() {
  useLanguage();
  const can = useCan();
  const { data: me } = useMe();
  const year = Number(me?.ranch ? localDate(new Date(), me.ranch.timezone).slice(0, 4) : new Date().getFullYear());
  const [season, setSeason] = useState(String(year));
  const [stage, setStage] = useState<Stage>("weaning");
  const [windowDays, setWindowDays] = useState("90");
  const [splitTreated, setSplitTreated] = useState(false);
  const q = useApi<CgResponse>(`/m/genetics/contemporary?season=${season}&stage=${stage}&window=${windowDays}&split_treated=${splitTreated ? 1 : 0}`);
  const [moves, setMoves] = useState<Record<string, { to: string; reason: string }>>({});
  const [moving, setMoving] = useState<{ id: string; from: string } | null>(null);
  const [to, setTo] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const groups = useMemo(() => (q.data ? applyMoves(q.data.groups, moves) : []), [q.data, moves]);
  const reset = () => setMoves({});
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post<{ groups: Array<{ revised: boolean }> }>("/m/genetics/contemporary", { stage, season, groups });
      await invalidateAll();
      setMoves({});
      toast(plural(r.groups.length, "Saved {{count}} group", "Saved {{count}} groups"), { tone: "good" });
    } catch (err) { toast(err instanceof ApiError ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" }); } finally { setBusy(false); }
  };
  const calves = q.data?.calves ?? {};
  const movedCount = Object.keys(moves).length;
  return (
    <GeneticsShell title={t("Contemporary groups")} subtitle={t("Calves that had the same chance to perform, compared together.")}>
      <div className="filter-row">
        <Select aria-label={t("Season")} value={season} onChange={(e) => { setSeason(e.target.value); reset(); }}>{[year, year - 1, year - 2].map((y) => <option key={y} value={y}>{y}</option>)}</Select>
        <Segmented label={t("Stage")} value={stage} onChange={(v) => { setStage(v); reset(); }} options={[{ value: "birth", label: t("Birth") }, { value: "weaning", label: t("Weaning") }, { value: "yearling", label: t("Yearling") }]} />
        <Select aria-label={t("Birth window")} value={windowDays} onChange={(e) => { setWindowDays(e.target.value); reset(); }}>{["60", "90", "120"].map((d) => <option key={d} value={d}>{t("Born within {{count}} days", { count: d })}</option>)}</Select>
      </div>
      <Toggle checked={splitTreated} onChange={(v) => { setSplitTreated(v); reset(); }} label={t("Keep calves treated for sickness apart")} hint={t("Uses health records. Off by default; associations differ.")} />
      {q.isLoading ? <Loading /> : !q.data ? <ErrorNote error={q.error} retry={() => q.refetch()} /> : !Object.keys(calves).length ? <Empty icon="group" title={t("No calves born in {{season}}", { season })} /> : (
        <>
          {q.data.saved.length > 0 && (
            <Section title={t("Saved")}>
              <List>{q.data.saved.map((s) => <Row key={s.event_id} icon="group" tone="genetics" to={s.group ? `/cattle/groups/${s.group.id}` : `/history/${s.event_id}`} title={t("Group {{code}}", { code: s.code })} meta={[plural(s.members, "{{count}} calf", "{{count}} calves"), s.adjusted ? plural(s.adjusted, "{{count}} moved by hand", "{{count}} moved by hand") : null, s.on ? formatDate(s.on) : null].filter(Boolean).join(" · ")} />)}</List>
            </Section>
          )}
          <Section title={q.data.saved.length ? t("Proposed from current records") : t("Proposed groups")} count={groups.length} hint={t("Split by management group, sex and birth window. ET calves and twins stay apart.")}>
            <div className="cards">
              {groups.map((g) => {
                const prop = q.data!.groups.find((p) => p.code === g.code)!;
                return (
                  <Card key={g.code}>
                    <div className="stack" style={{ gap: 8 }}>
                      <div className="genetics-row-head"><span className="genetics-qty">{g.code}</span><span className="muted">{plural(g.members.length, "{{count}} calf", "{{count}} calves")}</span></div>
                      <div className="genetics-targets">{prop.factors.map((f, i) => <Chip key={i} tone={f.kind === "management" || f.kind === "sex" ? "genetics" : "neutral"}>{factorText(f)}</Chip>)}</div>
                      <div className="choice-chips">
                        {g.members.map((m) => (
                          <button key={m} type="button" className="choice" disabled={!can("record")} title={moves[m] ? moves[m].reason : undefined} onClick={() => { setMoving({ id: m, from: g.code }); setTo(""); setReason(moves[m]?.reason ?? ""); }}>
                            {calves[m]?.label ?? m}{moves[m] ? " *" : ""}
                          </button>
                        ))}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
            {movedCount > 0 && <p className="muted">{plural(movedCount, "{{count}} calf moved by hand (marked *)", "{{count}} calves moved by hand (marked *)")}</p>}
          </Section>
          {q.data.unplaced.length > 0 && (
            <Section title={t("Can't group yet")} count={q.data.unplaced.length} hint={t("These calves have no birth date.")}>
              <div className="choice-chips">{q.data.unplaced.map((u) => <Link key={u.id} to={`/cattle/${u.id}`} className="choice">{calves[u.id]?.label ?? u.id}</Link>)}</div>
            </Section>
          )}
          {can("record") && (
            <div className="form-actions">
              <Button variant="primary" size="lg" icon="check" busy={busy} onClick={() => void save()}>{q.data.saved.length ? t("Save changes") : t("Save groups")}</Button>
              {movedCount > 0 && <Button variant="ghost" onClick={reset}>{t("Undo moves")}</Button>}
            </div>
          )}
        </>
      )}
      <Sheet open={!!moving} onClose={() => setMoving(null)} title={moving ? t("Move {{calf}}", { calf: calves[moving.id]?.label ?? "" }) : ""}>
        {moving && (
          <form onSubmit={(e) => {
            e.preventDefault();
            if (!to || !reason.trim()) return;
            const original = q.data!.groups.find((g) => g.members.includes(moving.id))?.code;
            const next = { ...moves };
            if (to === original) delete next[moving.id]; else next[moving.id] = { to, reason: reason.trim() };
            setMoves(next);
            setMoving(null);
          }}>
            <Field label={t("Into group")}>
              <div className="choice-chips">{groups.filter((g) => g.code !== moving.from).map((g) => <button key={g.code} type="button" className={`choice${to === g.code ? " choice-on" : ""}`} onClick={() => setTo(g.code)}>{g.code}</button>)}</div>
            </Field>
            <Field label={t("Why")} hint={t("Kept with the group so the association and buyers can see it.")}><TextInput value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("Raised on a nurse cow")} /></Field>
            <div className="form-actions"><Button variant="primary" size="lg" type="submit" disabled={!to || !reason.trim()}>{t("Move")}</Button></div>
          </form>
        )}
      </Sheet>
    </GeneticsShell>
  );
}
